import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { Typography, Button, Fade, Stack, Box } from '@mui/material';
import { useAuth } from 'providers/AuthProvider';
import { weddingPaths } from 'routes/paths';
import { adaptCoupleToUser } from 'services/api/adapters';
import { resolvePlanFromParam } from 'data/plan-config';
import { setPendingPlan } from 'utils/pendingPlan';
import { parseApiError } from 'utils/api-client';
import CreateEventForm, {
  CreateEventFormValues,
  CreateEventType,
} from 'components/sections/authentications/default/CreateEventForm';

interface CreateEventProps {
  // Fixed by the route (one route per event type) — there is no in-form toggle.
  eventType: CreateEventType;
}

type CreateResponse = {
  success: boolean;
  token: string;
  refreshToken: string;
  couple: {
    id: string;
    email: string;
    eventId: string;
    displayName: string;
    isEmailVerified?: boolean;
  };
  event: {
    eventId: string;
    name: string;
    dateISO: string;
    eventType?: CreateEventType;
    plan: 'free' | 'basic' | 'pro';
    maxBytes: number;
    hasJoinCode: boolean;
  };
  joinCode: string;
};

const CreateEvent = ({ eventType }: CreateEventProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [searchParams] = useSearchParams();
  const selectedPlan = resolvePlanFromParam(searchParams.get('plan'));

  const [result, setResult] = useState<CreateResponse | null>(null);

  const handleCreateEvent = async (values: CreateEventFormValues) => {
    // Map the i18next locale ('enUS' | 'elGR') back to the BE's two-letter
    // enum so transactional emails (verify, welcome, ...) honor the language
    // the user signed up in.
    const preferredLanguage = i18n.language?.startsWith('el') ? 'el' : 'en';

    const partner1 = values.partnerName1.trim();
    const partner2 = values.partnerName2.trim();
    const babyName = values.babyName?.trim() || '';

    // The event name leads with whoever the celebration is "about": the couple
    // for weddings, the baby for baptisms, both for the combined case. The type
    // is fixed by the route (the `eventType` prop), not user-editable in-form.
    let eventName: string;
    if (eventType === 'baptism') {
      eventName = babyName || `${partner1} & ${partner2}`;
    } else if (eventType === 'wedding_baptism' && babyName) {
      eventName = `${partner1} & ${partner2} · ${babyName}`;
    } else {
      eventName = `${partner1} & ${partner2}`;
    }

    const response = await fetch(`${import.meta.env.VITE_API_URL}/api/couple/create-free-event`, {
      method: 'POST',
      // Carries the `agapae_attr` cookie — the server's own authoritative
      // attribution record — into the ONE request that can associate it. This is
      // the single account-creation point in the product, so it is also the only
      // request where first-touch attribution can ever be attached to a couple;
      // `fetch` defaults to `same-origin` and app.agapae.gr → api.agapae.gr is
      // cross-ORIGIN, so without this the cookie is silently withheld and every
      // couple is created unattributed. Nothing else here needs cookies, and no
      // other call in the app sends them — see
      // docs/analytics/attribution-foundation.md.
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: values.email,
        password: values.password,
        eventName,
        eventType,
        partnerName1: partner1,
        partnerName2: partner2,
        ...(babyName ? { babyName } : {}),
        eventDateISO: values.eventDate,
        preferredLanguage,
        ...(values.turnstileToken ? { turnstileToken: values.turnstileToken } : {}),
      }),
    });

    if (!response.ok) {
      throw await parseApiError(response, t('auth.create_event.error_default'));
    }

    const data = await response.json();

    // Store result but DON'T set session yet
    // This prevents GuestGuard from redirecting before user sees the success state
    setResult(data);

    // Stash the chosen plan so VerifyEmail can pick it up after the
    // user verifies their email — paid upgrades require a verified
    // account, so we can't kick off Stripe here.
    if (selectedPlan !== 'free') {
      setPendingPlan(selectedPlan);
    }
  };

  const handleContinue = async () => {
    if (!result) return;

    try {
      // Adapt the couple response to User format. Stamp the event type onto the
      // session so post-signup copy (e.g. the verify-email message) reads in the
      // right voice immediately, before the first /couple/me hydration.
      const user = { ...adaptCoupleToUser(result), eventType: result.event.eventType };

      // Set session with token and refreshToken
      setSession(user, result.token, result.refreshToken);

      // Navigate to email verification
      navigate(weddingPaths.coupleVerifyEmail);
    } catch (error) {
      console.error('Failed to set session:', error);
      navigate(weddingPaths.coupleVerifyEmail);
    }
  };

  if (result) {
    return (
      <Fade in={true} timeout={800}>
        <Stack
          direction="column"
          sx={{
            flex: 1,
            minHeight: 1,
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', md: 'center' },
            p: { xs: 4, md: 10 },
            textAlign: 'left',
          }}
        >
          <Box sx={{ maxWidth: 420, width: 1 }}>
            <Typography variant="h4" sx={{ mb: 1 }}>
              {t('auth.create_event_success.emoji_title')}
            </Typography>
            <Typography variant="h2" sx={{ mb: 2 }}>
              {t('auth.create_event_success.title')}
            </Typography>
            <Typography sx={{ mb: 6, color: 'text.secondary', lineHeight: 1.6 }}>
              {t('auth.create_event_success.subtitle')}
            </Typography>

            <Button
              variant="contained"
              color="neutral"
              size="large"
              fullWidth
              onClick={handleContinue}
              sx={{ py: '14px' }}
            >
              {t('auth.create_event_success.verify_button')}
            </Button>
          </Box>
        </Stack>
      </Fade>
    );
  }

  return (
    <CreateEventForm
      eventType={eventType}
      handleCreateEvent={handleCreateEvent}
      loginLink={weddingPaths.coupleLogin}
    />
  );
};

export default CreateEvent;
